import { NavigationEnd, Router } from '@angular/router'
import { Subscription } from 'rxjs'
import { filter } from 'rxjs/operators'
import { Logger } from './../utils/logger'
import { ScreenNameProvider } from './screen-name-provider'
import { UserIdProvider } from './user-id-provider'

export class SimpleAnalytics {
    private static _logger: Logger | null = null
    private static _isEnabled = false
    private static screenNameProvider: ScreenNameProvider | null = null
    private static userIdProvider: UserIdProvider | null = null
    private static routerSubscription: Subscription | null = null
    private static appName: string
    private static identifyUser = false

    public static provideLogger(logger: Logger) {
        this._logger = logger
    }

    public static provideScreenNameProvider(screenNameProvider: ScreenNameProvider) {
        this.screenNameProvider = screenNameProvider
    }

    public static enable() {
        this._isEnabled = true
    }

    public static disable() {
        this._isEnabled = false
    }

    public static isEnabled(): boolean {
        return this._isEnabled
    }

    public static enableUserIdentification(idProvider: UserIdProvider) {
        this.userIdProvider = idProvider
        this.identifyUser = true
        this.log(`Enabled user tracking (${idProvider.getUserId()})`)
    }

    public static disableUserIdentification() {
        this.userIdProvider = null
        this.identifyUser = false
        this.log('Disabled user tracking')
    }

    public static setup(
        appName: string,
        router: Router,
        screenNameProvider: ScreenNameProvider | null,
        logger: Logger | null = null
    ) {
        this.appName = appName
        this._logger = logger
        this.screenNameProvider = screenNameProvider
        this.setupScreenTracking(router)
        this.log(`Simple Analytics initialized for ${appName}`)
    }

    public static tearDown() {
        this.routerSubscription?.unsubscribe()
    }

    public static trackEvent(eventName: string, data: Record<string, any> = {}) {
        this.log('EVENT:', eventName, data)
        if (!this._isEnabled) return

        //payload and fetch URL formatted in accordance with https://docs.simpleanalytics.com/events/server-side:

        const payload = {
            type: 'event',
            hostname: window.location.hostname,
            event: eventName,
            ua: window.navigator.userAgent,
            user_id: this.identifyUser ? this.userIdProvider?.getUserId() : undefined,
            ...data,
        }

        fetch('https://queue.simpleanalyticscdn.com/events', {
            method: 'POST',
            body: JSON.stringify(payload),
            keepalive: true,
            headers: {
                'Content-Type': 'application/json',
            },
        })
    }

    public static trackLink(eventName: string, link: { text: string; href: string }, data: Record<string, any> = {}) {
        this.trackEvent(eventName, { ...link, ...data })
    }

    public static trackScreen(screenName: string, extraData?: Record<string, any>) {
        this.log('SCREEN VIEW:', screenName, extraData)
        this.trackEvent('screen.view', {
            screen_name: screenName,
            app_name: this.appName,
            ...extraData,
        })
    }

    private static setupScreenTracking(router: Router) {
        const navigationStream = router.events.pipe(filter((event) => event instanceof NavigationEnd))

        this.routerSubscription = navigationStream.subscribe((event) => {
            if (!(event instanceof NavigationEnd)) return
            const url = event.urlAfterRedirects || event.url
            const screenName = this.screenNameProvider?.getScreenNameFromUrl(url) || url
            this.trackScreen(screenName, { path: url })
        })
    }

    private static log(...args: any[]) {
        this._logger?.info(...args)
    }
}

export function TrackEvent(eventName: string) {
    return function (target: any, propertyKey: string, descriptor: PropertyDescriptor) {
        const originalMethod = descriptor.value

        descriptor.value = function (...args: any[]) {
            // Optionally send event before method logic
            SimpleAnalytics.trackEvent(eventName)

            // Call the original method
            return originalMethod.apply(this, args)
        }

        return descriptor
    }
}
